import useChat from 'src/services/useChat';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { useAlert } from 'src/hooks/useAlert';
import {
  setBooleanConversation,
  setChatInput,
  setChatMode,
  setConversation,
  setPictureInPictureMode,
  TChatMode,
  updateLastBooleanMessageAnswer,
  updateLastMessageAnswer
} from 'src/features/chatNow';
import {
  AIAssistantPositionType,
  reducerUpdateOpenAIAssistant,
  reducerUpdatePositionAIAssistant
} from 'src/features/AIAssistantPosition';
import { toogleSidebar } from 'src/features/sidebarToggleSlice';
import { useEffect } from 'react';

export const useChatV2 = () => {
  const { handleClickAlert } = useAlert();
  const dispatch = useAppDispatch();

  const {
    useSendMessage,
    useGetConversationId,
    useGetBooleanConversationId,
    useSendBooleanMessage
  } = useChat();

  const { id: projectId } = useAppSelector((state) => state.users.project);
  const [sendMessage, { loading: sendingChatNowLoading }] = useSendMessage();

  const [sendBooleanMessage, { loading: sendingBooleanLoading }] =
    useSendBooleanMessage();

  const {
    conversation,
    chatMode,
    chatInput: chatInputValue,
    booleanConversation
  } = useAppSelector((state) => state.chatNow);

  const { position: positionAIAssistant } = useAppSelector(
    (state) => state.storeAIAssistantPosition
  );

  const [
    getConversationId,
    { data: conversationId, loading: chatNowLoading, refetch: refetchChatNow }
  ] = useGetConversationId();

  const [
    getBooleanConversationId,
    {
      data: booleanConversationIdData,
      loading: booleanLoading,
      refetch: refetchChatBoolean
    }
  ] = useGetBooleanConversationId();

  const isLoading = chatNowLoading || booleanLoading;
  const sendingLoading = sendingChatNowLoading || sendingBooleanLoading;

  const handleSendChatNowMessage = async (content: string) => {
    if (!conversationId) return;
    dispatch(
      setConversation([
        ...conversation,
        {
          id: `${conversation.length + 1}`,
          question: content,
          answer: '',
          createdAt: new Date().toISOString()
        }
      ])
    );
    dispatch(setChatInput(''));

    sendMessage({
      variables: {
        content: content,
        conversationId: conversationId?.chat_getConversationId.conversationId,
        projectId
      },
      onCompleted: (data) => {
        if (!data) {
          handleClickAlert({
            horizontal: 'center',
            vertical: 'top',
            message: 'Error occured while sending message',
            severity: 'error'
          });
          return;
        }

        dispatch(
          updateLastMessageAnswer({
            answer: data.chat_chatPost.answer,
            createdAt: data.chat_chatPost.createdAt
          })
        );
      },
      onError: (error) => {
        console.log(error);
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Error occured while sending message',
          severity: 'error'
        });
      }
    });
  };

  const handleSendBooleanQueryMessage = async (content: string) => {
    if (!booleanConversationIdData) return;
    dispatch(
      setBooleanConversation([
        ...booleanConversation,
        {
          id: `${booleanConversation.length + 1}`,
          question: content,
          response: '',
          createdAt: new Date().toISOString()
        }
      ])
    );
    dispatch(setChatInput(''));

    sendBooleanMessage({
      variables: {
        content: content,
        conversationId:
          booleanConversationIdData?.chat_getBooleanConversationId
            .conversationId,
        projectId: projectId
      },
      onCompleted: (data) => {
        if (!data) {
          handleClickAlert({
            horizontal: 'center',
            vertical: 'top',
            message: 'Error occured while sending message',
            severity: 'error'
          });
          return;
        }
        dispatch(updateLastBooleanMessageAnswer(data.chat_chatPostBoolean));
      },
      onError: (error) => {
        console.log(error);
        handleClickAlert({
          horizontal: 'center',
          vertical: 'top',
          message: 'Error occured while sending message',
          severity: 'error'
        });
      }
    });
  };

  const handleSendMessage =
    chatMode === 'chatNow'
      ? handleSendChatNowMessage
      : handleSendBooleanQueryMessage;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setChatInput(event.target.value));
  };

  const handleClickTopic = (topic: TChatMode) => {
    dispatch(setChatMode(topic));
  };

  const handleChangePositionMode = (mode: AIAssistantPositionType) => {
    dispatch(reducerUpdatePositionAIAssistant(mode));
    if (mode === 'window' || mode === 'full') {
      dispatch(setPictureInPictureMode(true));
      dispatch(toogleSidebar(true));
    } else {
      dispatch(setPictureInPictureMode(false));
      dispatch(toogleSidebar(false));
    }
  };

  const handleCloseAIAssistant = () => {
    dispatch(reducerUpdatePositionAIAssistant('side'));
    dispatch(reducerUpdateOpenAIAssistant(false));
    dispatch(toogleSidebar(true));
    if (positionAIAssistant === 'window' || positionAIAssistant === 'full') {
      dispatch(setPictureInPictureMode(false));
    }
  };

  const handleClickNewChat = async () => {
    try {
      await refetchChatNow();
      await refetchChatBoolean();
      dispatch(setConversation([]));
      dispatch(setBooleanConversation([]));
      dispatch(setChatInput(''));
      dispatch(setChatMode(null));
    } catch (err) {
      console.error('Refetch failed:', err);
    }
  };

  useEffect(() => {
    getConversationId();
    getBooleanConversationId();
  }, []);

  return {
    chatInputValue,
    isLoading,
    sendingLoading,
    handleSendMessage,
    handleChange,
    handleClickTopic,
    handleSendChatNowMessage,
    handleSendBooleanQueryMessage,
    handleChangePositionMode,
    handleCloseAIAssistant,
    handleClickNewChat
  };
};
